import FullScreenLoader from "../components/utils/FullScreenLoader"
import { MyDispatch, MySelector } from "../redux/store"
import { useLocation, useNavigate } from "react-router-dom"
import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { getPhysicalAlbums } from "../redux/gallerySlice"
import { setActiveAlbumId, setFolderType } from "../redux/utilSlice"
import { PhysicalAlbum as PhysicalAlbumType } from "../types/physicalAlbum"
import folderIcon from "../assets/folder.svg"

const PhysicalAlbum = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<MyDispatch>()

  const { physicalAlbums } = MySelector((state) => state.gallery)
  const { category, eventId, userId, verified, config } = MySelector(
    (state) => state.event
  )

  // not allowed for guests
  useEffect(() => {
    if (!category || !location || !eventId) return
    if (category === "guest") navigate(`/${eventId}/gallery`)
  }, [category, location, eventId, navigate])

  // mark folder type + clear any active album when on the list
  useEffect(() => {
    dispatch(setFolderType("physical"))
    dispatch(setActiveAlbumId(null))
  }, [dispatch])

  // fetch albums (also covers direct navigation / reload to this page)
  useEffect(() => {
    if (category !== "host") return
    if (!eventId || !config) return
    if (config?.dataCollectionEnabled && !verified) return
    dispatch(
      getPhysicalAlbums({ eventId: eventId || "", userId: userId || undefined })
    )
  }, [category, eventId, userId, verified, config, dispatch])

  if (!physicalAlbums) return <FullScreenLoader />

  return (
    <div className="flex flex-col flex-1 items-center mt-[75px] lg:mt-0 w-full px-4 lg:px-10">
      <div className="w-full max-w-[1400px]">
        <div className="flex justify-between items-center my-4 lg:mt-6 lg:mb-10 gap-3">
          <p className="font-[500] text-newOffBlack dark:text-offWhite text-base md:text-lg xl:text-xl">
            Physical Albums
          </p>
        </div>

        {physicalAlbums.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 mt-24">
            <img src={folderIcon} alt="" className="h-12 w-12 lg:h-16 lg:w-16" />
            <p className="text-sm lg:text-lg text-blackish dark:text-grey">
              No physical albums yet
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-10">
            {physicalAlbums.map((album: PhysicalAlbumType) => (
              <div
                key={album.id}
                onClick={() => {
                  dispatch(setActiveAlbumId(album.id))
                  navigate(`/${eventId}/physical-album/${album.id}`)
                }}
                className="cursor-pointer rounded-2xl overflow-hidden border-[0.5px] border-offGrey/40 bg-white/40 dark:bg-white/5 hover:shadow-lg transition-all duration-150"
              >
                <div className="aspect-square w-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                  {album.coverPhotoUrl ? (
                    <img
                      src={album.coverPhotoUrl}
                      alt={album.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <img src={folderIcon} alt="" className="h-10 w-10 opacity-60" />
                    </div>
                  )}
                </div>

                <div className="p-3">
                  <p className="font-[500] text-sm text-newOffBlack dark:text-offWhite truncate">
                    {album.name}
                  </p>
                  <p className="text-xs text-offGrey dark:text-grey mt-0.5">
                    {album.fileCount || 0}{" "}
                    {(album.fileCount || 0) === 1 ? "photo" : "photos"}
                    {album.fileLimit ? ` / ${album.fileLimit}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default PhysicalAlbum
